import React, {useCallback, useRef} from "react";
import LoginSceneLayout from "~/layouts/LoginSceneLayout";
import ReactDocumentTitle from "~/components/ReactDocumentTitle";
import {createUseStyles} from "react-jss";
import UIButton from "~/old-ui/UIButton";
import UIInput from "~/old-ui/UIInput";
import UILink from "~/old-ui/UILink";
import {
  LOGIN_ERROR_TOKEN_INVALID_TYPE,
  LOGIN_ERROR_TOKEN_NOT_FOUND,
  useFetchSetPassword,
  useTokenData
} from "~/backend";
import UIForm from "~/old-ui/UIForm";
import {useHistory, useParams} from "react-router";
import {reverse} from "~/urls";
import LoginErrorNotification from "~/components/LoginErrorNotification";
import useFormState from "~/hooks/useFormState";
import NotFoundScene from "~/scenes/system/NotFoundScene";
import {mediaSizes} from "~/theme";

export default function ActivateScene() {
  const classes = useStyles();
  const history = useHistory();
  const {token} = useParams();

  const confirmNewPasswordRef = useRef();

  const [formData, setFormData] = useFormState({newPassword: '', confirmNewPassword: ''});

  const tokenData = useTokenData(token, 'activation');
  const [fetchSetPassword, fetchSetPasswordState, fetchSetPasswordResetState] = useFetchSetPassword();

  const onSubmit = useCallback(() => {
    if (formData.newPassword !== formData.confirmNewPassword) {
      confirmNewPasswordRef.setCustomValidity("Password confirmation don't match the password.");
      setFormData({confirmNewPassword: ''});
      confirmNewPasswordRef.current.focus();

      return;
    }

    fetchSetPassword({token: token, newPassword: formData.newPassword});
  }, [formData]);

  if (tokenData.loading) {
    return (<div/>);
  }

  if (tokenData.error === LOGIN_ERROR_TOKEN_INVALID_TYPE && tokenData.data && tokenData.data.type === 'reset-password') {
    history.replace(reverse('reset-set-password', {token}));
    return <div/>;
  }

  if (tokenData.error === LOGIN_ERROR_TOKEN_NOT_FOUND) {
    return (<NotFoundScene/>)
  }

  return (
    <>
      <ReactDocumentTitle title='Account activation'/>

      <LoginSceneLayout>
        {tokenData.error && (
          <LoginErrorNotification
            className={classes.centerNotification}
            errorCode={tokenData.error}
          />
        )}

        {!tokenData.error && (
          <>
            <h1 className={classes.h1}>Activate</h1>
            <div className={classes.h1Secondary}>
              {tokenData.data ? (<b>{tokenData.data.email}</b>) : 'your Workshop Butler'} account
            </div>

            {fetchSetPasswordState.error && (
              <LoginErrorNotification
                className={classes.notification}
                errorCode={fetchSetPasswordState.error}
                closable
                onClose={fetchSetPasswordResetState}
              />
            )}

            {!fetchSetPasswordState.data && (
              <div className={classes.content}>
                <UIForm preventDefault method='POST' onSubmit={onSubmit}>
                  <UIInput
                    large
                    name='newPassword'
                    type='password'
                    required={true}
                    value={formData.newPassword}
                    onChange={(e) => setFormData({newPassword: e.target.value})}
                    className={classes.formInput}
                    placeholder='Enter new password'
                    autoComplete='new-password'
                  />

                  <UIInput
                    large
                    ref={confirmNewPasswordRef}
                    name='confirmNewPassword'
                    type='password'
                    required={true}
                    value={formData.confirmNewPassword}
                    onChange={(e) => setFormData({confirmNewPassword: e.target.value})}
                    className={classes.formInput}
                    placeholder='Re-enter new password'
                    autoComplete='new-password'
                  />

                  <div className={classes.termOfUse}>
                    By activating the account, you agree with Workshop Butler's <UILink href='https://workshopbutler.com/terms/' target='_blank'>
                    Terms of use</UILink> and <UILink href='https://workshopbutler.com/privacy/' target='_blank'>Privacy policy</UILink>.
                  </div>
                  <UIButton primary large className={classes.formSubmitBtn} disabled={fetchSetPasswordState.loading}>
                    Set password
                  </UIButton>
                </UIForm>

              </div>
            )}
          </>
        )}

      </LoginSceneLayout>
    </>
  )
}

const useStyles = createUseStyles({
  h1: {
    fontSize: 24,
    fontWeight: 500,
    textAlign: 'center',
    margin: [[12, 0, 8]]
  },

  h1Secondary: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 60,
  },

  content: {
    width: 280,
    marginLeft: 'auto',
    marginRight: 'auto',

    [mediaSizes['xs']]: {
      width: 'auto',
    },
  },

  formInput: {
    marginBottom: 12,
  },

  termOfUse: {
    padding: 12,
  },

  formSubmitBtn: {
    display: 'block',
    width: '100%',
    height: 44,
  },

  notification: {
    marginTop: -32,
    marginBottom: 32,
    width: 380,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  centerNotification: {
    marginTop: 100,
    width: 380,
    marginLeft: 'auto',
    marginRight: 'auto',
  }
});
